import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';

import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Typography
} from '@mui/joy';

import ResponsiveLayout from '../components/layout/ResponsiveLayout';
import TableHeader from '../components/TableHeader';
import { DroneProfileService } from '../services/DroneProfileService';
import { DroneProfile, DroneHeartBeat } from '../models/DroneProfile/DroneProfile.model';
import organizationStore from '../stores/OrganizationStore';

// Import new dashboard components
import {
  SystemHealthSummary,
  RecentCampaignHighlights,
  FleetActivityTimeline,
  CriticalAlertsPanel,
  ResourceAvailability,
  UpcomingScheduledCampaigns,
  PerformanceMetrics,
  WeatherImpactIndicator,
  TeamActivityFeed,
  QuickAccessShortcuts,
  MaverickAIChat
} from '../components/DashboardBeta';

const DashboardBeta = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedOrg } = organizationStore;

  const [drones, setDrones] = useState<DroneProfile[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [heartbeats, setHeartbeats] = useState<{ [droneId: string]: DroneHeartBeat | null }>({});
  const [selectedDrone, setSelectedDrone] = useState<string | null>(null);

  useEffect(() => {
    const fetchDrones = async () => {
      if (!selectedOrg) {
        setError(t('drones.error_no_org'));
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const res = await DroneProfileService.getOrganizationDroneProfiles(selectedOrg);
        const droneList = res.data.drone_profiles || [];
        setDrones(droneList);

        if (droneList.length > 0 && !selectedDrone) {
          setSelectedDrone(droneList[0].id);
        }

        // Fetch heartbeats for each drone
        const heartbeatResults = await Promise.all(
          droneList.map(async (drone) => {
            try {
              const hbRes = await DroneProfileService.getDroneHeartBeat(drone.id);
              if (hbRes && hbRes.success && hbRes.data) {
                return [drone.id, hbRes.data as DroneHeartBeat];
              }
              return [drone.id, null];
            } catch (e) {
              return [drone.id, null];
            }
          })
        );
        setHeartbeats(Object.fromEntries(heartbeatResults));
      } catch (err) {
        setError(t('drones.error_fetch'));
      } finally {
        setLoading(false);
      }
    };
    fetchDrones();
  }, [selectedOrg, t, selectedDrone]);

  // Helper function to determine battery status - used in the commented out section
  // Keeping this for reference in case we need to restore the original dashboard
  /*
  const getBatteryStatus = (heartbeat: DroneHeartBeat | null) => {
    if (!heartbeat || heartbeat.battery_percentage === undefined) return { value: null, color: 'neutral' };

    const battery = heartbeat.battery_percentage;
    if (battery > 70) return { value: battery, color: 'success' };
    if (battery > 30) return { value: battery, color: 'warning' };
    return { value: battery, color: 'danger' };
  };
  */

  // Helper function to determine connection status
  const getConnectionStatus = (heartbeat: DroneHeartBeat | null) => {
    if (!heartbeat) return { status: 'offline', color: 'danger' };

    // Check how recent the heartbeat is
    const lastHeartbeat = new Date(heartbeat.timestamp);
    const now = new Date();
    const diffMinutes = (now.getTime() - lastHeartbeat.getTime()) / (1000 * 60);

    if (diffMinutes < 5) return { status: 'online', color: 'success' };
    if (diffMinutes < 30) return { status: 'idle', color: 'warning' };
    return { status: 'offline', color: 'danger' };
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }



  // These variables are used in the hidden original dashboard section
  // Keeping them commented for reference
  // const selectedDroneData = drones.find(d => d.id === selectedDrone);
  // const selectedHeartbeat = selectedDrone ? heartbeats[selectedDrone] : null;
  // If we restore the original dashboard, we'll need to uncomment the getBatteryStatus function above
  // const batteryStatus = getBatteryStatus(selectedHeartbeat);
  // const connectionStatus = getConnectionStatus(selectedHeartbeat);

  return (
    <ResponsiveLayout>
      {error && (
        <Typography color="danger" level="body-lg" sx={{ textAlign: 'center', mt: 2 }}>{error}</Typography>
      )}
      <TableHeader title={t('dashboard.beta_title') || 'Dashboard (Beta)'}>
        <Box sx={{ display: 'flex', alignItems: 'center', marginLeft: 'auto', gap: 1 }}>
          <Button color='neutral' onClick={() => navigate('/register-drone')} variant='solid'>
            {t('drones.add_device', 'Add Device')}
          </Button>
        </Box>
      </TableHeader>

      <Grid container spacing={2} sx={{ mt: 1 }}>
        {/* Main Grid Container - All components */}
        <Grid container spacing={2}>

        <Grid xs={12} md={12} lg={12}>
            <QuickAccessShortcuts />
          </Grid>
          {/* First Row */}
          <Grid xs={12} md={6} lg={4}>
            <SystemHealthSummary
              totalDrones={drones.length}
              onlineDrones={drones.filter(d => getConnectionStatus(heartbeats[d.id]).status === 'online').length}
              offlineDrones={drones.filter(d => getConnectionStatus(heartbeats[d.id]).status === 'offline').length}
              warningDrones={drones.filter(d => getConnectionStatus(heartbeats[d.id]).status === 'idle').length}
            />
          </Grid>



          <Grid xs={12} md={6} lg={4}>
            <CriticalAlertsPanel />
          </Grid>

          <Grid xs={12} md={12} lg={4}>
            <MaverickAIChat />
          </Grid>




          {/* Third Row */}
          <Grid xs={12} md={6} lg={6}>
            <RecentCampaignHighlights />
          </Grid>

          <Grid xs={12} md={6} lg={6}>
            <UpcomingScheduledCampaigns />
          </Grid>
          <Grid xs={12} md={12} lg={12}>
            <WeatherImpactIndicator />
          </Grid>
          <Grid xs={12} md={12} lg={12}>
            <PerformanceMetrics />
          </Grid>



        </Grid>
      </Grid>


    </ResponsiveLayout>
  );
});

export default DashboardBeta;